import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { BrowserRouter, HashRouter, Navigate, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { ErrorBoundary } from './components/ErrorBoundary';
import { TabBar } from './components/TabBar';
import { PageSkeleton, ToastHost } from './components/ui';
import { AccountSheet } from './components/AccountSheet';
import { BootSplash } from './components/BootSplash';
import { initAccount } from './state/accountSync';
import { RecipeGate } from './components/RecipeGate';
import { initReminders } from './state/reminderSync';
import { track } from './lib/analytics';
import { useStore } from './state/store';
import { Today } from './screens/Today';
import { Setup, Welcome } from './screens/Welcome';

// Secondary screens are split out of the first-load bundle.
const Plan = lazy(() => import('./screens/Plan').then((m) => ({ default: m.Plan })));
const List = lazy(() => import('./screens/List').then((m) => ({ default: m.List })));
const You = lazy(() => import('./screens/You').then((m) => ({ default: m.You })));
const Scan = lazy(() => import('./screens/Scan').then((m) => ({ default: m.Scan })));
const Review = lazy(() => import('./screens/Review').then((m) => ({ default: m.Review })));
const Kitchen = lazy(() => import('./screens/Kitchen').then((m) => ({ default: m.Kitchen })));
const Favorites = lazy(() => import('./screens/Favorites').then((m) => ({ default: m.Favorites })));
const RecipeScreen = lazy(() => import('./screens/Recipe').then((m) => ({ default: m.RecipeScreen })));
const Cook = lazy(() => import('./screens/Cook').then((m) => ({ default: m.Cook })));
const More = lazy(() => import('./screens/More').then((m) => ({ default: m.More })));
const Occasion = lazy(() => import('./screens/Occasion').then((m) => ({ default: m.Occasion })));
const Done = lazy(() => import('./screens/Done').then((m) => ({ default: m.Done })));
const Plus = lazy(() => import('./screens/Plus').then((m) => ({ default: m.Plus })));
const Ask = lazy(() => import('./screens/Ask').then((m) => ({ default: m.Ask })));

function useTheme() {
  const theme = useStore((s) => s.theme);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const apply = () => {
      const dark = theme === 'dark' || (theme === 'system' && mq.matches);
      document.documentElement.classList.toggle('dark', dark);
      document.querySelector('meta[name="theme-color"]')?.setAttribute('content', dark ? '#000000' : '#FFFFFF');
    };
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [theme]);
}

function RequireOnboarded({ children }: { children: ReactNode }) {
  const onboarded = useStore((s) => s.onboarded);
  return onboarded ? <>{children}</> : <Navigate to="/welcome" replace />;
}

function TabsLayout() {
  return (
    <>
      {/* Its own boundary, so the tab bar stays put while a tab's code loads. */}
      <Suspense fallback={<PageSkeleton />}><Outlet /></Suspense>
      <TabBar />
    </>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  // Braces matter: newer browsers return a Promise from scrollTo, and an effect's return value is its cleanup.
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Preview pages are hosted at a fixed URL, so they route inside the hash.
const Router = import.meta.env.MODE === 'artifact' ? HashRouter : BrowserRouter;

const Loading = () => <PageSkeleton />;

/** Schedules reminders and opens the screen a tapped reminder points to. */
function ReminderBridge() {
  const navigate = useNavigate();
  useEffect(() => { initReminders((route) => navigate(route)); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

export default function App() {
  useTheme();
  const onboarded = useStore((s) => s.onboarded);
  const recovered = useStore((s) => s.recovered);
  const showToast = useStore((s) => s.showToast);
  const archiveEndedPlan = useStore((s) => s.archiveEndedPlan);

  useEffect(() => {
    track('app_open');
    const since = useStore.getState().onboardedAt;
    const days = since ? Math.floor((Date.now() - new Date(since).getTime()) / 86_400_000) : 0;
    if (onboarded && days >= 1) track('returned_after_onboarding', { days });
    archiveEndedPlan();
    void initAccount();
    if (recovered) showToast('We couldn’t read your saved data, so we started fresh.', { tone: 'error' });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ErrorBoundary>
      <Router>
        <ScrollToTop />
        <ReminderBridge />
        <div className="app-shell">
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route path="/" element={<Navigate to={onboarded ? '/today' : '/welcome'} replace />} />
              <Route path="/welcome" element={onboarded ? <Navigate to="/today" replace /> : <Welcome />} />
              <Route path="/setup" element={<Navigate to="/setup/1" replace />} />
              <Route path="/setup/:step" element={<Setup />} />
              <Route element={<RequireOnboarded><TabsLayout /></RequireOnboarded>}>
                <Route path="/today" element={<Today />} />
                <Route path="/plan" element={<Plan />} />
                <Route path="/kitchen" element={<Kitchen />} />
                <Route path="/favorites" element={<Favorites />} />
                <Route path="/you/saved" element={<Navigate to="/favorites" replace />} />
                <Route path="/list" element={<List />} />
                <Route path="/you" element={<You />} />
                <Route path="/you/:section" element={<You />} />
              </Route>
              {/* Old paths from earlier versions and shared links. */}
              <Route path="/tonight" element={<Navigate to="/today" replace />} />
              <Route path="/me" element={<Navigate to="/you" replace />} />
              <Route path="/scan" element={<RequireOnboarded><Scan /></RequireOnboarded>} />
              <Route path="/review" element={<RequireOnboarded><Review /></RequireOnboarded>} />
              <Route path="/recipe/:id" element={<RequireOnboarded><RecipeGate><RecipeScreen /></RecipeGate></RequireOnboarded>} />
              <Route path="/cook/:id" element={<RequireOnboarded><RecipeGate><Cook /></RecipeGate></RequireOnboarded>} />
              <Route path="/done/:id" element={<RequireOnboarded><Done /></RequireOnboarded>} />
              <Route path="/more/:section" element={<RequireOnboarded><More /></RequireOnboarded>} />
              <Route path="/occasion" element={<RequireOnboarded><Occasion /></RequireOnboarded>} />
              <Route path="/plus" element={<Plus />} />
              <Route path="/ask" element={<RequireOnboarded><Ask /></RequireOnboarded>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          <ToastHost />
          <AccountSheet />
        </div>
        <BootSplash />
      </Router>
    </ErrorBoundary>
  );
}
